import '@shoelace-style/shoelace/dist/components/popup/popup.js';
import enEmojiTranslations from 'emoji-picker-element/i18n/en.js';
import 'emoji-picker-element';
import { ApplicationElement, html } from '../application_element/element.js';
import style from './style.lit.css';
import translations from './translations.js';
import '../icon/element.js';

/**
 * A button that shows/hides emoji picker. When emoji is picked it will dispatch an "emoji-click" event (e.details will show all you need about picked emoji).
 *
 * @prop open - Show emoji picker by default? (default false), will be calculated internally
 *
 * @slot default - Button for opening/closing emoji-picker
 */

export default class EmojiButton extends ApplicationElement {
  static styles = [ApplicationElement.styles, style];

  static properties = {
    open: { type: Boolean },
    emojiTranslations: { type: Object, state: true },
  };

  /** @internal */
  static translations = translations;

  constructor() {
    super();
    this.emojiTranslations = enEmojiTranslations;
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('click', this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this);
  }

  willUpdate(prevProps) {
    super.willUpdate(prevProps);

    if (prevProps.has('AppConfig')) {
      this.initEmojiTranslations();
    }

    if (prevProps.get('open')) {
      this._emojiElements = null;
    }
  }

  initEmojiTranslations() {
    let translationsFile = this.i18n.locale.split('-')[0];
    if (translationsFile === 'en') return; // en is imported as default in constructor

    this.constructor.langImports ||= {};
    this.constructor.langImports[translationsFile] ||= import(
      `emoji-picker-element/i18n/${translationsFile}.js`
    );

    this.constructor.langImports[translationsFile].then(t => {
      this.emojiTranslations = t.default;
    });
  }

  /** @internal */
  get emojiElements() {
    const { pickerShadowRoot } = this;

    this._emojiElements ||= Array.from(
      pickerShadowRoot.querySelectorAll('.nav > *, .emoji-menu > *')
    );

    return this._emojiElements;
  }

  get picker() {
    return this.shadowRoot?.querySelector('emoji-picker');
  }

  get trigger() {
    return this.shadowRoot.querySelector('button');
  }

  /** @internal */
  get pickerShadowRoot() {
    return this.picker?.shadowRoot;
  }

  handleEvent(e) {
    // close on click outside
    if (e.type === 'click' && !e.target.closest('ef-emoji-button'))
      this.open = false;
  }

  /* c8 ignore next 68 */
  handleKey(e) {
    const { pickerShadowRoot } = this;

    if (!pickerShadowRoot) return;

    e.stopPropagation();

    // Force the same tab order on all browsers - safari navigation would never focus any emoji via Tab, making the rest of this function useless.
    // So the first focused element is search input (focused on open), after it, tab focuses first usable emoji
    if (
      e.key === 'Tab' &&
      pickerShadowRoot.activeElement.tagName === 'INPUT' &&
      this.emojiElements[9]
    ) {
      this.emojiElements[9].focus();
      e.preventDefault();
      return;
    }

    // Alpha-numeric focuses the input and resets _emojiElement cache
    if (/^[0-9a-zA-Z]$/.test(e.key)) {
      pickerShadowRoot.querySelector('input').focus();
      this._emojiElements = null;
      return;
    }

    // Escape closes
    if (e.key === 'Escape') {
      this.open = false;
      return;
    }

    // Arrows navigate
    const { activeElement } = pickerShadowRoot;
    const activeIndex = this.emojiElements.indexOf(activeElement);
    const rowLength = activeIndex < 9 ? 9 : 8; // 8 emojis fit in one full body and footer row, 9 in header row

    // In case a header (category) emoji is selected, reset _emojiElements cache
    if (activeIndex < 9 && (e.key === 'Enter' || e.code === 'Space')) {
      this._emojiElements = null;
      return;
    }

    if (activeIndex >= 0) {
      let newActive;

      switch (e.key) {
        case 'ArrowUp':
          newActive = this.emojiElements[activeIndex - rowLength];
          break;

        case 'ArrowDown':
          newActive = this.emojiElements[activeIndex + rowLength];
          break;

        case 'ArrowLeft':
          newActive = this.emojiElements[activeIndex - 1];
          break;

        case 'ArrowRight':
          newActive = this.emojiElements[activeIndex + 1];
      }

      if (newActive) {
        // Prevent default scroll on Arrow keys, newActive will be automatically scrolled to when focused
        e.preventDefault();
        newActive.focus();
      }
    }
  }

  handleClick() {
    this.open = !this.open;
    this.open &&
      setTimeout(() => this.pickerShadowRoot.querySelector('input').focus()); // timeOut allows picker to open and initialize
  }

  handleEmojiClick() {
    this.open = false;
  }

  stopPropagation(e) {
    e.stopPropagation();
  }

  renderPicker() {
    // @input=${this.stopPropagation} is needed because the picker can be placed inside trix-editor,
    // which listens to input and gets confused when it gets input from emoji-picker
    return html`
      <emoji-picker
        @keydown=${this.handleKey}
        @emoji-click=${this.handleEmojiClick}
        @input=${this.stopPropagation}
        @keypress=${this.stopPropagation}
        .i18n=${this.emojiTranslations}>
      </emoji-picker>
    `;
  }

  render() {
    return html`
      <sl-popup
        ?active=${this.open}
        placement="top-end"
        flip
        flip-fallback-strategy="best-fit">
        <slot slot="anchor" @click=${this.handleClick}>
          <button
            aria-label=${this.t('choose_emoji')}
            aria-haspopup="true"
            aria-expanded=${String(!!this.open)}>
            <ef-icon name="smile"></ef-icon>
          </button>
        </slot>
        ${this.open ? this.renderPicker() : null}
      </sl-popup>
    `;
  }
}

EmojiButton.registerTagName('ef-emoji-button');

customElements.get('ef-emoji-button') ||
  customElements.define('ef-emoji-button', EmojiButton);
